import React from 'react';
import { View, StyleSheet, Pressable } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { ThemedText, Body, Caption } from '@/components/ui/Text';

type MapEmptyStateProps = {
  type: 'no-trip' | 'no-itinerary' | 'no-pins' | 'no-search-results' | 'mapbox-missing';
  variant?: 'fullscreen' | 'card';
  onAction?: () => void;
  actionLabel?: string;
  onSecondaryAction?: () => void;
};

export const MapEmptyState: React.FC<MapEmptyStateProps> = ({
  type,
  variant = 'fullscreen',
  onAction,
  actionLabel,
  onSecondaryAction,
}) => {
  const theme = useTheme();

  const getContent = () => {
    switch (type) {
      case 'no-trip':
        return {
          icon: 'map-outline',
          title: 'Select a Trip',
          description: 'Choose a trip from the dropdown above to view its map and discover places along your journey.',
          showAction: false,
          secondaryAction: null,
        };
      case 'no-itinerary':
        return {
          icon: 'calendar-outline',
          title: 'No Itinerary Items Yet',
          description: 'Start planning your trip! Add activities, restaurants, and places to visit. They\'ll appear on the map so you can see your journey at a glance.',
          showAction: true,
          defaultActionLabel: 'Add to Itinerary',
          secondaryAction: {
            label: 'Browse landmarks',
            icon: 'star-outline',
          },
        };
      case 'no-pins':
        return {
          icon: 'location-outline',
          title: 'No Locations Found',
          description: 'No locations are available to display on the map for this trip yet. Add locations to itinerary items, or browse landmarks.',
          showAction: true,
          defaultActionLabel: 'Add locations',
          secondaryAction: {
            label: 'Browse Landmarks',
            icon: 'star-outline',
          },
        };
      case 'no-search-results':
        return {
          icon: 'location-outline',
          title: 'No Places Found',
          description: 'We couldn’t find any places to show right now. Try browsing landmarks or adding locations to your itinerary.',
          showAction: false,
          secondaryAction: null,
        };
      case 'mapbox-missing':
        return {
          icon: 'map-outline',
          title: 'Maps are offline',
          description: 'Live maps aren’t available yet. You can still manage your itinerary and saved places, then retry setup when you’re ready.',
          showAction: true,
          defaultActionLabel: 'Setup Maps',
          secondaryAction: {
            label: 'Retry',
            icon: 'refresh',
          },
        };
      default:
        return {
          icon: 'map-outline',
          title: 'Map',
          description: '',
          showAction: false,
          secondaryAction: null,
        };
    }
  };

  const content = getContent();
  const isCard = variant === 'card';

  return (
    <View style={[styles.container, isCard ? styles.containerCard : null]}>
      <View
        style={[
          styles.iconContainer,
          {
            backgroundColor: theme.colors.primary + '15',
            ...(isCard ? { width: 72, height: 72, borderRadius: 36, marginBottom: 0 } : null),
          },
        ]}
      >
        <Ionicons
          name={content.icon as any}
          size={isCard ? 36 : 48}
          color={theme.colors.primary}
        />
      </View>
      <ThemedText variant="h3" style={[styles.title, { color: theme.colors.text }, isCard ? { marginBottom: 0 } : null]}>
        {content.title}
      </ThemedText>
      <Body color={theme.colors.textSecondary} style={[styles.description, isCard ? { maxWidth: 340 } : null]}>
        {content.description}
      </Body>
      {(content.showAction || content.secondaryAction) && (
        <View style={styles.actionsContainer}>
          {content.showAction && onAction && (
            <Pressable
              onPress={onAction}
              style={({ pressed }) => [
                styles.button,
                styles.primaryButton,
                {
                  backgroundColor: theme.colors.primary,
                  borderColor: theme.colors.primary,
                  opacity: pressed ? 0.85 : 1,
                },
              ]}
            >
              <ThemedText variant="bodyStrong" style={{ color: theme.colors.onPrimary }}>
                {actionLabel || content.defaultActionLabel || 'Action'}
              </ThemedText>
            </Pressable>
          )}
          {content.secondaryAction && onSecondaryAction && (
            <Pressable
              onPress={onSecondaryAction}
              style={({ pressed }) => [
                styles.button,
                styles.secondaryButton,
                {
                  backgroundColor: 'transparent',
                  borderColor: theme.colors.border,
                  opacity: pressed ? 0.85 : 1,
                },
              ]}
            >
              <Ionicons 
                name={content.secondaryAction.icon as any} 
                size={18} 
                color={theme.colors.primary} 
                style={styles.secondaryIcon}
              />
              <ThemedText variant="body" style={{ color: theme.colors.primary }}>
                {content.secondaryAction.label}
              </ThemedText>
            </Pressable>
          )}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
    gap: 16,
  },
  containerCard: {
    flex: 0,
    padding: 16,
    gap: 12,
  },
  iconContainer: {
    width: 96,
    height: 96,
    borderRadius: 48,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
  },
  title: {
    textAlign: 'center',
    marginBottom: 8,
  },
  description: {
    textAlign: 'center',
    maxWidth: 300,
    lineHeight: 20,
  },
  actionsContainer: {
    marginTop: 8,
    gap: 12,
    width: '100%',
    alignItems: 'center',
  },
  button: {
    minWidth: 200,
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 12,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    gap: 8,
  },
  primaryButton: {
    // Primary button styles
  },
  secondaryButton: {
    // Secondary button styles
  },
  secondaryIcon: {
    marginRight: 4,
  },
});

